import { useEffect, useRef } from 'react';
import {
  GenerationResponse,
  getGenerationStatus,
} from '../../services/generationsApi';
import { checkSongReadyDual } from '../../utils/streamReadyChecker';

interface UseDualSongCheckingParams {
  taskId: string | null;
  enabled: boolean;
  musicGeneration?: GenerationResponse;
  onSongReady: (
    songVersion: 1 | 2,
    url: string,
    source: 'polling' | 'stream',
  ) => void;
}

export const useDualSongChecking = ({
  taskId,
  enabled,
  musicGeneration,
  onSongReady,
}: UseDualSongCheckingParams) => {
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasNotifiedSong1Ref = useRef(false);
  const hasNotifiedSong2Ref = useRef(false);

  useEffect(() => {
    if (!enabled || !taskId) {
      return;
    }

    const linkedTaskId = musicGeneration?.linkedTaskId;
    const conversionId1 = musicGeneration?.conversionId1;
    const conversionId2 = musicGeneration?.conversionId2;

    if (!linkedTaskId || (!conversionId1 && !conversionId2)) {
      return;
    }

    const checkSongs = async () => {
      // Check if songs are ready
      const song1Ready =
        hasNotifiedSong1Ref.current || !!musicGeneration?.songUrl1;
      const song2Ready =
        hasNotifiedSong2Ref.current || !!musicGeneration?.songUrl2;

      // Stop interval if both songs are ready
      if (song1Ready && song2Ready) {
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
        return;
      }

      try {
        const getPollingStatus = async () => {
          const status = await getGenerationStatus(taskId);
          return {
            songUrl1: status.songUrl1,
            songUrl2: status.songUrl2,
          };
        };

        // Only check song1 if it's not ready yet
        if (conversionId1 && !song1Ready) {
          const result = await checkSongReadyDual(
            linkedTaskId,
            conversionId1,
            getPollingStatus,
            1,
          );

          if (result && result.url) {
            hasNotifiedSong1Ref.current = true;
            onSongReady(1, result.url, result.source);
          }
        }

        // Only check song2 if it's not ready yet
        if (conversionId2 && !song2Ready) {
          const result = await checkSongReadyDual(
            linkedTaskId,
            conversionId2,
            getPollingStatus,
            2,
          );

          if (result && result.url) {
            hasNotifiedSong2Ref.current = true;
            onSongReady(2, result.url, result.source);
          }
        }

        // Stop interval if both songs are now ready
        const bothReadyNow =
          (hasNotifiedSong1Ref.current || !!musicGeneration?.songUrl1) &&
          (hasNotifiedSong2Ref.current || !!musicGeneration?.songUrl2);

        if (bothReadyNow && checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[Dual Song Checking] Error:', error);
        }
      }
    };

    const checkInterval = 3000;

    checkSongs();
    checkIntervalRef.current = setInterval(checkSongs, checkInterval);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [
    enabled,
    taskId,
    musicGeneration?.linkedTaskId,
    musicGeneration?.conversionId1,
    musicGeneration?.conversionId2,
    musicGeneration?.songUrl1,
    musicGeneration?.songUrl2,
    onSongReady,
  ]);

  useEffect(() => {
    if (musicGeneration?.songUrl1) {
      hasNotifiedSong1Ref.current = true;
    }
    if (musicGeneration?.songUrl2) {
      hasNotifiedSong2Ref.current = true;
    }
  }, [musicGeneration?.songUrl1, musicGeneration?.songUrl2]);
};
